import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Users, SlidersHorizontal, X, Search, ArrowDownUp } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
} from "@dnd-kit/core";
import api from "../lib/api";
import { KANBAN_COLUMNS, formatRange, relativeTime } from "../lib/format";

function OppCard({ opp, dragging }) {
  return (
    <div
      className={`ts-card p-4 cursor-grab active:cursor-grabbing transition ${dragging ? "rotate-1 ring-1 ring-[#C9A84C]/60" : ""}`}
      data-testid={`opp-card-${opp.id}`}
    >
      <Link to={`/opportunities/${opp.id}`} onClick={(e) => e.stopPropagation()} className="block">
        <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] mb-2">
          {opp.client_type || "Unspecified client"}
        </div>
        <div className="font-serif text-base text-[#F0EDE8] leading-snug line-clamp-2">{opp.title}</div>
      </Link>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {(opp.sectors_involved || []).slice(0, 2).map((s) => (
          <span key={s} className="text-[10px] uppercase tracking-widest text-[#9A9590] border border-[#C9A84C]/20 rounded-full px-2 py-0.5">
            {s}
          </span>
        ))}
        {opp.sectors_involved?.length > 2 && (
          <span className="text-[10px] text-[#9A9590]">+{opp.sectors_involved.length - 2}</span>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-[#9A9590]">
        <span className="font-serif text-[#F0EDE8] text-sm">
          {formatRange(opp.estimated_value_min, opp.estimated_value_max)}
        </span>
        <span className="inline-flex items-center gap-1">
          <Users size={12} className="text-[#C9A84C]" />
          {(opp.interested_member_ids || []).length}
        </span>
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-widest text-[#9A9590]/70">
        {opp.posted_by_user?.name || "Member"} · {relativeTime(opp.created_at)}
      </div>
    </div>
  );
}

function Draggable({ id, children }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} style={{ opacity: isDragging ? 0.3 : 1 }}>
      {children}
    </div>
  );
}

function Column({ col, items }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.key });
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-col-${col.key}`}
      className={`snap-center shrink-0 w-[280px] sm:flex-1 sm:w-auto sm:min-w-[260px] rounded-lg p-4 transition-colors ${
        isOver ? "bg-[rgba(201,168,76,0.06)]" : "bg-[#141414]"
      } border ${isOver ? "border-[#C9A84C]/55" : "border-[#C9A84C]/12"}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="overline">{col.label}</div>
        <div className="text-xs text-[#9A9590]">{items.length}</div>
      </div>
      <div className="space-y-3 min-h-[100px]">
        {items.map((opp) => (
          <Draggable key={opp.id} id={opp.id}>
            <OppCard opp={opp} />
          </Draggable>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-[#9A9590]/60 italic py-6 text-center">Empty</div>
        )}
      </div>
    </div>
  );
}

const SORT_OPTIONS = [
  { key: "recent", label: "Most recent" },
  { key: "value_desc", label: "Value (high → low)" },
  { key: "value_asc", label: "Value (low → high)" },
  { key: "interest", label: "Most interest" },
];

export default function Opportunities() {
  const [opps, setOpps] = useState([]);
  const [members, setMembers] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState("");
  const [sectorFilter, setSectorFilter] = useState([]); // multi
  const [clientFilter, setClientFilter] = useState([]); // multi
  const [posterFilter, setPosterFilter] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    const [o, m, prefs] = await Promise.all([
      api.get("/opportunities"),
      api.get("/members"),
      api.get("/me/preferences").catch(() => ({ data: {} })),
    ]);
    setOpps(o.data);
    setMembers(m.data);
    const saved = (prefs.data || {}).kanban_opportunities || {};
    if (saved.query) setQuery(saved.query);
    if (Array.isArray(saved.sectorFilter)) setSectorFilter(saved.sectorFilter);
    if (Array.isArray(saved.clientFilter)) setClientFilter(saved.clientFilter);
    if (saved.posterFilter) setPosterFilter(saved.posterFilter);
    if (saved.sortBy) setSortBy(saved.sortBy);
    if (saved.filtersOpen != null) setFiltersOpen(saved.filtersOpen);
    setHydrated(true);
  };
  useEffect(() => {
    document.title = "Opportunities · The Synergy Table";
    load();
  }, []);

  // Persist filters server-side once user state changes — debounced.
  useEffect(() => {
    if (!hydrated) return;
    const payload = { query, sectorFilter, clientFilter, posterFilter, sortBy, filtersOpen };
    const handle = setTimeout(() => {
      api.patch("/me/preferences/kanban_opportunities", payload).catch(() => {});
    }, 600);
    return () => clearTimeout(handle);
  }, [hydrated, query, sectorFilter, clientFilter, posterFilter, sortBy, filtersOpen]);

  const sectors = useMemo(
    () => Array.from(new Set(opps.flatMap((o) => o.sectors_involved || []))),
    [opps]
  );
  const clientTypes = useMemo(
    () => Array.from(new Set(opps.map((o) => o.client_type).filter(Boolean))),
    [opps]
  );

  const filtered = useMemo(() => {
    let result = opps;
    if (query) {
      const q = query.toLowerCase();
      result = result.filter(
        (o) =>
          o.title?.toLowerCase().includes(q) ||
          o.description?.toLowerCase().includes(q) ||
          (o.sectors_involved || []).join(" ").toLowerCase().includes(q)
      );
    }
    if (sectorFilter.length) {
      result = result.filter((o) =>
        (o.sectors_involved || []).some((s) => sectorFilter.includes(s))
      );
    }
    if (clientFilter.length) {
      result = result.filter((o) => clientFilter.includes(o.client_type));
    }
    if (posterFilter) {
      result = result.filter((o) => o.posted_by === posterFilter);
    }
    const sortVal = (o) => o.estimated_value_max || o.estimated_value_min || 0;
    const sorters = {
      recent: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      value_desc: (a, b) => sortVal(b) - sortVal(a),
      value_asc: (a, b) => sortVal(a) - sortVal(b),
      interest: (a, b) => (b.interested_member_ids?.length || 0) - (a.interested_member_ids?.length || 0),
    };
    return [...result].sort(sorters[sortBy]);
  }, [opps, query, sectorFilter, clientFilter, posterFilter, sortBy]);

  const toggle = (arr, setArr, v) =>
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const clearAll = () => {
    setQuery(""); setSectorFilter([]); setClientFilter([]); setPosterFilter(""); setSortBy("recent");
  };

  const activeFilterCount = sectorFilter.length + clientFilter.length + (posterFilter ? 1 : 0) + (query ? 1 : 0);

  const onDragStart = (e) => setActiveId(e.active.id);
  const onDragEnd = async (e) => {
    setActiveId(null);
    const oppId = e.active.id;
    const newStatus = e.over?.id;
    if (!newStatus) return;
    const opp = opps.find((o) => o.id === oppId);
    if (!opp || opp.status === newStatus) return;
    setOpps((prev) => prev.map((o) => (o.id === oppId ? { ...o, status: newStatus } : o)));
    try {
      await api.patch(`/opportunities/${oppId}`, { status: newStatus });
      const colLabel = KANBAN_COLUMNS.find((c) => c.key === newStatus)?.label || newStatus;
      toast.success(`Moved to ${colLabel}`);
    } catch {
      toast.error("Could not update status. Refreshing…");
      load();
    }
  };

  const active = activeId ? opps.find((o) => o.id === activeId) : null;

  return (
    <div className="space-y-8" data-testid="opportunities-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="overline">Pipeline</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F0EDE8] mt-2 tracking-tight">Opportunities</h1>
          <p className="text-[#B8B3AC] mt-3 max-w-xl">
            Every deal starts as an opportunity at the table. Drag a card across the pipeline as it evolves.
          </p>
        </div>
        <Link to="/opportunities/new" className="btn-gold px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm self-start" data-testid="new-opportunity">
          <Plus size={16} /> Post an Opportunity
        </Link>
      </div>

      {/* Controls bar */}
      <div className="ts-card p-4 flex flex-wrap items-center gap-3" data-testid="kanban-controls">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9590]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, description, sector…"
            className="w-full bg-transparent border border-[#C9A84C]/20 rounded-md pl-9 pr-3 py-2 text-sm text-[#F0EDE8] placeholder-[#9A9590]/60 focus:outline-none focus:border-[#C9A84C]/60"
            data-testid="kanban-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <ArrowDownUp size={14} className="text-[#9A9590]" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-transparent border border-[#C9A84C]/20 rounded-md px-3 py-2 text-sm text-[#F0EDE8] focus:outline-none focus:border-[#C9A84C]/60"
            data-testid="kanban-sort"
          >
            {SORT_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <button
          onClick={() => setFiltersOpen((o) => !o)}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm border transition ${
            filtersOpen || activeFilterCount > 0
              ? "border-[#C9A84C] text-[#C9A84C]"
              : "border-[#C9A84C]/25 text-[#9A9590]"
          }`}
          data-testid="kanban-filter-toggle"
        >
          <SlidersHorizontal size={14} /> Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 text-[10px] bg-[#C9A84C] text-[#0D0D0D] rounded-full px-1.5 py-0.5">{activeFilterCount}</span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <button onClick={clearAll} className="text-xs text-[#9A9590] hover:text-[#C9A84C] inline-flex items-center gap-1" data-testid="kanban-clear">
            <X size={12}/> Clear
          </button>
        )}
      </div>

      {filtersOpen && (
        <div className="ts-card p-6 space-y-5 animate-fade-in" data-testid="kanban-filters">
          <div>
            <div className="overline mb-2">Sectors</div>
            <div className="flex flex-wrap gap-2">
              {sectors.map((s) => (
                <button
                  key={s}
                  onClick={() => toggle(sectorFilter, setSectorFilter, s)}
                  className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest transition ${
                    sectorFilter.includes(s) ? "bg-[#C9A84C] text-[#0D0D0D]" : "border border-[#C9A84C]/25 text-[#9A9590] hover:border-[#C9A84C]/55"
                  }`}
                >
                  {s}
                </button>
              ))}
              {sectors.length === 0 && <span className="text-xs text-[#9A9590]">No sectors yet.</span>}
            </div>
          </div>
          <div>
            <div className="overline mb-2">Client type</div>
            <div className="flex flex-wrap gap-2">
              {clientTypes.map((c) => (
                <button
                  key={c}
                  onClick={() => toggle(clientFilter, setClientFilter, c)}
                  className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest transition ${
                    clientFilter.includes(c) ? "bg-[#C9A84C] text-[#0D0D0D]" : "border border-[#C9A84C]/25 text-[#9A9590] hover:border-[#C9A84C]/55"
                  }`}
                >
                  {c}
                </button>
              ))}
              {clientTypes.length === 0 && <span className="text-xs text-[#9A9590]">No client types yet.</span>}
            </div>
          </div>
          <div>
            <div className="overline mb-2">Posted by</div>
            <select
              value={posterFilter}
              onChange={(e) => setPosterFilter(e.target.value)}
              className="bg-transparent border border-[#C9A84C]/25 rounded-md px-3 py-2 text-sm text-[#F0EDE8] focus:outline-none focus:border-[#C9A84C]/60"
              data-testid="kanban-poster"
            >
              <option value="">Anyone</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        </div>
      )}

      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2 snap-x snap-mandatory lg:snap-none">
          {KANBAN_COLUMNS.map((col) => (
            <Column key={col.key} col={col} items={filtered.filter((o) => o.status === col.key)} />
          ))}
        </div>
        <DragOverlay>{active ? <OppCard opp={active} dragging /> : null}</DragOverlay>
      </DndContext>
    </div>
  );
}
